// @ts-nocheck

import { isEqual } from 'lodash';
import { createSelector } from 'reselect';

import { getMatchIdFromProps } from '../../selectors/_propsGetters_';
import { selectEntitiesBetCategories } from '../../selectors/entities/betCategories/entitiesBetCategoriesPrimary.selector';
import { selectEntitiesMatchesEntityHotPeriod } from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesOutcomes } from '../../selectors/entities/outcomes/entitiesOutcomesPrimary.selector';
import { selectEntitiesPlayers } from '../../selectors/entities/players/entitiesPlayersPrimary.selector';
import { makeComplexeMatchBetsSelector, makeMatchBetsArraySelector } from '../../selectors/match.selector';
import { selectRuntimeLicence } from '../../selectors/runtime/runtimePrimary.selector';
import { BetCategories } from '../../types/bet';
import { Licences } from '../../types/common';
import {
  MatchBetFiltersPatterns,
  MatchBetFiltersPatternsPrefixes,
  getMatchBetFilterPattern,
} from '../../types/matchBetFilters';
import { getSrPlayerIdsFromSBV } from '../../utils/bet';
import { createDeepEqualSelector, createShallowEqualSelector } from '../../utils/reselect';

const BetCategoriesWithNewTag = {
  [Licences.FR]: [BetCategories.PLAYER_PROPS, BetCategories.COMPETITOR_PROPS],
};

const makeMatchGroupsWithBetFiltersPropsSelector = () => {
  const selectMatchBets = makeMatchBetsArraySelector();

  const selectMatchBetCategoryIds = createShallowEqualSelector(selectMatchBets, (matchBets) =>
    matchBets.reduce(
      (acc, bet) => (Array.isArray(bet.betCategories) ? [...new Set([...acc, ...bet.betCategories])] : acc),
      [],
    ),
  );

  const selectMatchBetCategories = createShallowEqualSelector(
    [selectMatchBetCategoryIds, selectEntitiesBetCategories],
    (betCategoryIds, betCategories) =>
      betCategoryIds.reduce(
        (acc, betCategoryId) => (betCategories[betCategoryId] ? [...acc, betCategories[betCategoryId]] : acc),
        [],
      ),
  );

  const affectPlayerToFiltersMap = (srPlayerId, players, filtersMap, displayOrder, betCategoryId) => {
    if (srPlayerId) {
      const player = players.filter((k) => k).find((p) => p.srPlayerId === srPlayerId);

      if (player) {
        const { playerId, name, shortname } = player;
        const currFilter = filtersMap[player.playerId];

        filtersMap[player.playerId] = !currFilter
          ? {
              id: getMatchBetFilterPattern(MatchBetFiltersPatternsPrefixes.PLAYER_ID, playerId),
              label: shortname ?? name,
              position: displayOrder,
              nbBets: 1,
              parentId: getMatchBetFilterPattern(MatchBetFiltersPatternsPrefixes.BET_CATEGORY, betCategoryId),
            }
          : {
              ...currFilter,
              nbBets: currFilter.nbBets + 1,
            };
      }
    }
  };

  const sortMatchBetFilters = (matchBetFilters, playersMap) => {
    const recSorting = (prev, next) => {
      if (prev.filters || next.filters) {
        /* Recursion */
        if (prev.filters) {
          prev.filters = prev.filters.sort(recSorting);
        }

        if (next.filters) {
          next.filters = next.filters.sort(recSorting);
        }
      } else {
        /* Actual nested sorting algorithm */
        const { id: prevId } = prev;
        const { id: nextId } = next;

        const playerFilterPattern = MatchBetFiltersPatterns[MatchBetFiltersPatternsPrefixes.PLAYER_ID];

        const areTheyPlayersFilters = playerFilterPattern.test(prevId) && playerFilterPattern.test(nextId);

        if (areTheyPlayersFilters) {
          const prevPlayerId = parseInt(prevId.match(playerFilterPattern)?.[1], 10);
          const nextPlayerId = parseInt(nextId.match(playerFilterPattern)?.[1], 10);

          if (!Number.isNaN(prevPlayerId) && !Number.isNaN(nextPlayerId)) {
            const prevPlayer = playersMap[prevPlayerId];
            const nextPlayer = playersMap[nextPlayerId];

            if (prevPlayer && nextPlayer) {
              const { displayOrder: prevPlayerDisplayOrder } = prevPlayer;
              const { displayOrder: nextPlayerDisplayOrder } = nextPlayer;

              if (prevPlayerDisplayOrder && nextPlayerDisplayOrder) {
                return prevPlayerDisplayOrder - nextPlayerDisplayOrder;
              }
            }
          }
        }

        return next.nbBets - prev.nbBets;
      }
    };

    for (const matchBetFilter of matchBetFilters) {
      const { filters } = matchBetFilter;

      if (Array.isArray(filters)) {
        filters.sort(recSorting);
      }
    }

    return matchBetFilters;
  };

  const selectComplexeMatchBets = makeComplexeMatchBetsSelector();

  const selectMatchBetFilters = createSelector(
    [
      selectMatchBetCategories,
      selectComplexeMatchBets,
      selectMatchBetCategoryIds,
      selectEntitiesPlayers,
      selectEntitiesOutcomes,
      selectRuntimeLicence,
    ],
    (matchBetCategories, matchBets, betCategoryIds, playersMap, outcomesMap, licence) => {
      const filteredBetsByCategories = matchBets.map((matchBet) => matchBet.bet.betCategories);
      const hasAllBetsInAllFilters = filteredBetsByCategories.every((categories) =>
        isEqual(betCategoryIds, categories),
      );
      const playersMapKeys = Object.keys(playersMap);
      const players = playersMapKeys.map((key) => playersMap[key]);

      if (!hasAllBetsInAllFilters) {
        const result = [];

        for (const matchBetCategory of matchBetCategories) {
          const { betCategoryId, name, displayOrder } = matchBetCategory;
          const betCategoryMatchBets = matchBets.filter(
            (matchBet) => ~matchBet.bet.betCategories?.indexOf(betCategoryId),
          );
          const nbBets = betCategoryMatchBets.length;
          const showNewTag = BetCategoriesWithNewTag[licence]?.includes(betCategoryId) ?? false;

          if (
            betCategoryId === BetCategories.SCORERS_SOCCER ||
            betCategoryId === BetCategories.SCORERS_BASKETBALL ||
            betCategoryId === BetCategories.SCORERS_RUGBY ||
            betCategoryId === BetCategories.PLAYER_PROPS
          ) {
            const filtersMap = {};
            for (const betCategoryMatchBet of betCategoryMatchBets) {
              const {
                bet: { specialBetValue, outcomes: outcomeIds },
              } = betCategoryMatchBet;
              const srPlayerIds = getSrPlayerIdsFromSBV(specialBetValue);

              if (Array.isArray(srPlayerIds)) {
                for (const srPlayerId of srPlayerIds) {
                  affectPlayerToFiltersMap(srPlayerId, players, filtersMap, displayOrder, betCategoryId);
                }
              } else if (Array.isArray(outcomeIds)) {
                for (const outcomeId of outcomeIds) {
                  const { srPlayerId } = outcomesMap[outcomeId] ?? {};

                  affectPlayerToFiltersMap(srPlayerId, players, filtersMap, displayOrder, betCategoryId);
                }
              }
            }

            const filters = Object.keys(filtersMap)
              .filter((k) => k)
              .map((key) => filtersMap[key]);

            if (nbBets) {
              result.push({
                id: getMatchBetFilterPattern(MatchBetFiltersPatternsPrefixes.BET_CATEGORY, betCategoryId),
                label: name,
                position: displayOrder,
                newTag: showNewTag,
                nbBets,
                filters,
              });
            }

            if (!filters.length && result[result.length - 1].filters) {
              delete result[result.length - 1].filters;
            }
          } else {
            if (nbBets) {
              result.push({
                id: getMatchBetFilterPattern(MatchBetFiltersPatternsPrefixes.BET_CATEGORY, betCategoryId),
                label: name,
                position: displayOrder,
                newTag: showNewTag,
                nbBets,
              });
            }
          }
        }

        return sortMatchBetFilters(
          result.sort((prev, next) => prev.position - next.position),
          playersMap,
        );
      }

      return [];
    },
  );

  const selectNbMatchBets = createSelector([selectMatchBets], (matchBets) => matchBets.length);

  return createDeepEqualSelector(
    [selectMatchBetFilters, selectNbMatchBets, getMatchIdFromProps, selectEntitiesMatchesEntityHotPeriod],
    (matchBetFilters, nbMatchBets, matchId, matchHotPeriod) => ({
      matchBetFilters,
      nbMatchBets,
      matchId,
      matchHotPeriod,
    }),
  );
};

export default makeMatchGroupsWithBetFiltersPropsSelector;
